import React, { useState, useEffect, useContext } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, TextField,Container,Paper,Alert,Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { collection, query, where, getDocs,Timestamp } from 'firebase/firestore'; // Ensure Firebase is correctly initialized
import { db } from '../firebase';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from "date-fns";
import AppContext from '../AppContext';
import InfoIcon from '@mui/icons-material/Info';

function ShiftReport() {
  
  const [fromDate, setFromDate] = useState(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0); // Set time to 00:00:00
    return start;
  });
  
  const [toDate, setToDate] = useState(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999); // Set time to 23:59:59.999
    return end;
  });
  const [shifts, setShifts] = useState([]);
  const [chartData, setChartData] = useState([]);
  const {shopId, t}=useContext(AppContext);
 // const [fromDate,setFromDate]=useState(new Date());
 // const [toDate,setToDate]=useState(new Date());

  const handleFetchData = async (fromDate,toDate) => {
    const fromTimestamp = Timestamp.fromDate(fromDate);
  const toTimestamp = Timestamp.fromDate(toDate);
    const q = query(
      collection(db, 'shifts'),
      where('shiftStartTime','>=',fromTimestamp),
      where('shiftEndTime','<=',toTimestamp),
      where('shopId','==',shopId)
    );

    const querySnapshot = await getDocs(q);
    const fetchedShifts = [];
    const balanceByUser = {};
  //  console.log(querySnapshot.docs[0]);

    querySnapshot.forEach((doc, index) => {
      const data = doc.data();

      // Convert Firestore Timestamps to Date objects
      const shiftStartTime = data.shiftStartTime;
      const shiftEndTime = data.shiftEndTime;

      const balance = data.openingCash + data.cashPayments + data.paidIn - data.paidOut - data.refunds;

      // Add to fetched shifts for the table
      if(balance>=0)
      {
        fetchedShifts.push({
          ...data,
          shiftStartTime: shiftStartTime, // Convert to string
          shiftEndTime: shiftEndTime, // Convert to string
          balance
        });
  
        // Aggregate balance for the chart
        if (balanceByUser[data.userName]) {
          balanceByUser[data.userName].push(balance);
        } else {
          balanceByUser[data.userName] = [balance];
        }
      }
     
    });

    setShifts(fetchedShifts);
    console.log(fetchedShifts);

  const chartArray = [];
  for (const [userName, balances] of Object.entries(balanceByUser)) {
    if (balances.length === 1) {
      chartArray.push({ name: userName, amount: balances[0] });
    } else {
      balances.forEach((amount, index) => {
        chartArray.push({ name: `${userName} (#${index + 1})`, amount });
      });
    }
  }
  setChartData(chartArray);

    
  };

  useEffect(() => {
    handleFetchData(fromDate,toDate);
},[fromDate,toDate]);

  const formatter = new Intl.NumberFormat('en-GB', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const renderContent = () => {
    if (shifts.length === 0) {
      return (
        <Paper 
          elevation={0} 
          sx={{ 
            mt: 3, 
            p: 3, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
          }}
        >
          <Alert 
            icon={<InfoIcon />}
            severity="info"
            sx={{ 
              width: '100%',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="h6" component="div">
              No Data Available
            </Typography>
            <Typography variant="body1">
            {t('noShiftDataMessage')}
            </Typography>
          </Alert>
        </Paper>
      );
    }

    return (
      <>
        {/* Bar Chart */}
        <Box sx={{ mt: 5 }}>
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 10, right: 20, left: 40, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(value) => formatter.format(value)} />
              <YAxis type="category" dataKey="name" width={140} />
              <Tooltip formatter={(value, name) => [formatter.format(value), name]} />
              <Legend />
              <Bar dataKey="amount" name={t('amount')} fill="#1565C0" />
            </BarChart>
          </ResponsiveContainer>
        )}
        </Box>
        
        {/* Shifts Table */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('sr')}</TableCell>
              <TableCell>{t('shiftStartTime')}</TableCell>
              <TableCell>{t('shiftEndTime')}</TableCell>
              <TableCell>{t('userName')}</TableCell>
              <TableCell>{t('openingCash')}</TableCell>
              <TableCell>{t('cashPayment')}</TableCell>
              <TableCell>{t('refund')}</TableCell>
              <TableCell>{t('paidIn')}</TableCell>
              <TableCell>{t('paidOut')}</TableCell>
              <TableCell>{t('balance')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((shift, index) => {
             const shiftStartTime = shift.shiftStartTime
             ? format(new Date(shift.shiftStartTime.seconds * 1000), "dd/MM/yyyy hh:mm a")
             : "N/A";
           const shiftEndTime = shift.shiftEndTime
             ? format(new Date(shift.shiftEndTime.seconds * 1000), "dd/MM/yyyy hh:mm a")
             : "N/A";
              return(
              <TableRow key={shift.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{shiftStartTime}</TableCell>
                <TableCell>{shiftEndTime}</TableCell>
                <TableCell>{shift.userName}</TableCell>
                <TableCell>{formatter.format(shift.openingCash)}</TableCell>
                <TableCell>{formatter.format(shift.cashPayments)}</TableCell>
                <TableCell>{formatter.format(shift.refunds)}</TableCell>
                <TableCell>{formatter.format(shift.paidIn)}</TableCell>
                <TableCell>{formatter.format(shift.paidOut)}</TableCell>
                <TableCell>{formatter.format(shift.balance)}</TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container>
        <Box>
          {/* Date Pickers */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <DateTimePicker
          label={t("from")}
          value={fromDate}
          onChange={(newValue) => setFromDate(newValue)}
          format="dd/MM/yyyy hh:mm a" // Enforce the format here
          renderInput={(params) => <TextField {...params} />}
        />
            <DateTimePicker
          label={t("to")}
          value={toDate}
          onChange={(newValue) => setToDate(newValue)}
          format="dd/MM/yyyy hh:mm a" // Enforce the format here
          renderInput={(params) => <TextField {...params} />}
        />
          </Box>

          {renderContent()}
        </Box>
      </Container>
    </LocalizationProvider>
  );
}

export default ShiftReport;
